import { Directive, HostListener, OnInit, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, Subscription, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import { ApAnalyticsService, ClickAnalyticsData } from '@ap/analytics';
import { ApContentService } from '@ap/content';
import { ApDateService } from '@ap/date';
import { SessiebeheerResource, UserType } from '@ap/interface-apic-sessiebeheer';
import { MijnJwtService, MijnSession } from '@ap/mijn-auth-sessie-manager';
import { MijnNavigatieService } from '@ap/mijn-navigatie';
import { ApModalService, ModalClosedReason, ModalContainer, ModalOptions, ModalSize } from '@ap/modal';

import { MijnAnalyseModalComponent } from './modal/mijn-analyse.modal.component';
import content from './modal/mijn-analyse.modal.content.json';
import { MijnAnalyseOptions, MijnAnalyseSessionInformation } from './modal/mijn-analyse.modal.model';

@Directive({
    selector: '[mijn-analyse]',
    standalone: false
})
export class MijnAnalyseDirective implements OnInit {
    static mijnAnalyseInitialized = false;
    public isModalOpen = false;
    private subscriptions = new Subscription();
    private modalContainer: ModalContainer;
    private readonly SESSION_ID_CHARACTER_LIMIT = 5;

    public constructor(
        private readonly analyticsService: ApAnalyticsService,
        private readonly contentService: ApContentService,
        private readonly modalService: ApModalService,
        private readonly domSanitizer: DomSanitizer,
        private readonly mijnNavigatieService: MijnNavigatieService,
        private readonly sessiebeheerResource: SessiebeheerResource,
        private readonly mijnJwtService: MijnJwtService
    ) {}

    @HostListener('document:keydown.shift.control.z')
    public openAnalyseModal(): void {
        if (!MijnAnalyseDirective.mijnAnalyseInitialized || this.isModalOpen) {
            return;
        }

        this.sendAnalytics();

        this.subscriptions.add(
            this.getSession()
                .pipe(
                    map(
                        (session) =>
                            ({
                                sessionID: session?.sessionId
                                    ? session.sessionId.slice(session.sessionId.length - this.SESSION_ID_CHARACTER_LIMIT)
                                    : this.contentService.get('mijn.analyse.modal.form.sessionid.unknown'),

                                customerID: session?.userId
                                    ? session.userId
                                    : this.contentService.get('mijn.analyse.modal.form.customerid.unknown')
                            }) as MijnAnalyseSessionInformation
                    ),
                    tap(
                        (sessionInformation) => this.domSanitizer.sanitize(SecurityContext.HTML, sessionInformation),
                        catchError(() => of({ sessionID: null, customerID: null } as MijnAnalyseSessionInformation))
                    )
                )
                .subscribe((sessieID) => {
                    const modalOptions = new ModalOptions(this.getMijnAnalyseOptions(sessieID));
                    modalOptions.title = this.contentService.get('mijn.analyse.modal.title');
                    modalOptions.cancelable = true;
                    modalOptions.size = ModalSize.LARGE;
                    modalOptions.showHeader = true;

                    this.modalContainer = this.modalService.open(MijnAnalyseModalComponent, modalOptions);
                    this.isModalOpen = true;

                    this.subscriptions.add(
                        this.modalContainer.afterClosed().subscribe(() => {
                            this.isModalOpen = false;
                        })
                    );
                })
        );
    }

    @HostListener('window:popstate')
    public onPopState(): void {
        if (this.isModalOpen) {
            this.modalContainer.close(ModalClosedReason.BACK);
        }
    }

    public ngOnInit(): void {
        if (!MijnAnalyseDirective.mijnAnalyseInitialized) {
            this.contentService.addContentItems(content);

            MijnAnalyseDirective.mijnAnalyseInitialized = true;
        }
    }

    private getMijnAnalyseOptions(sessionInformation: MijnAnalyseSessionInformation): MijnAnalyseOptions {
        return {
            url: {
                label: this.contentService.get('mijn.analyse.modal.form.url'),
                value: this.getUrl()
            },
            breadcrumb: {
                label: this.contentService.get('mijn.analyse.modal.form.breadcrumb'),
                value: this.getBreadcrumbPath()
            },
            date: {
                label: this.contentService.get('mijn.analyse.modal.form.datetime'),
                value: ApDateService.utc()
            },
            browser: {
                label: this.contentService.get('mijn.analyse.modal.form.browser'),
                value: this.getBrowser()
            },
            session: {
                label: this.contentService.get('mijn.analyse.modal.form.sessionid'),
                value: sessionInformation.sessionID
            },
            customerID: {
                label: this.contentService.get('mijn.analyse.modal.form.customerid'),
                value: sessionInformation.customerID
            }
        };
    }

    private getUrl(): string {
        const result = this.domSanitizer.sanitize(SecurityContext.URL, window.location.href);
        return result ?? this.contentService.get('mijn.analyse.modal.form.url.unknown');
    }

    private getBreadcrumbPath(): string {
        const history = this.mijnNavigatieService.getHistory();
        if (history?.routes?.length > 0) {
            return history.routes.map((route) => route.breadcrumb).join(' - ');
        }
        return this.contentService.get('mijn.analyse.modal.form.breadcrumb.unknown');
    }

    private getBrowser(): string {
        const result = this.domSanitizer.sanitize(SecurityContext.HTML, window.navigator.userAgent);
        return result ?? this.contentService.get('mijn.analyse.modal.form.browser.unknown');
    }

    private getSession(): Observable<MijnSession | undefined> {
        return this.mijnJwtService.isAuthenticated$.pipe(
            take(1),
            switchMap((authenticated) => {
                if (authenticated) {
                    return this.mijnJwtService.customerId$.pipe(
                        take(1),
                        switchMap((userId) =>
                            this.mijnJwtService.contextId$.pipe(
                                take(1),
                                map((sessionId) => ({ userId, sessionId }))
                            )
                        )
                    );
                }

                return this.sessiebeheerResource.getSession('0').pipe(
                    catchError(() => of(undefined)),
                    map((session) =>
                        session?.user && !session.user.type.includes(UserType.ANONYMOUS)
                            ? { sessionId: session.id, userId: session.user.id }
                            : undefined
                    )
                );
            })
        );
    }

    private sendAnalytics(): void {
        this.analyticsService.push(new ClickAnalyticsData('mijn-analyse', 'open-modal'));
    }
}
